// @flow

import { compact, some, isNil, pick, without, isUndefined } from "lodash";
import OrderItem from "./order_item";
import { get, post, request } from "utils/ajax";
import type { CartPayload, OrderItemPayload } from "types";

type CartChangedHandler = CartPayload => void;
type postal_payload = { postal_code?: string, country_code?: string };
export default class Cart {
  id: string;
  order_items: Array<OrderItem>;
  subtotal: string;
  shipping_price: string;
  packaging_price: string;
  total: string;
  postal_code: string;
  country_code: string;
  cart_weight: number;
  recommendations: Array<OrderItemPayload>;

  static init(cart_id: string, cart_loaded: () => void): Cart {
    let cart = new Cart(cart_id);
    cart.populate_with_server_data(cart_loaded);
    return cart;
  }

  constructor(id: string) {
    this.id = id;
    this.order_items = [];
  }

  load_from_server(result: CartPayload) {
    this.id = result.id;
    this.order_items = compact(
      result.order_items.map(OrderItem.load_from_json)
    );
    this.subtotal = result.subtotal;
    this.shipping_price = result.shipping_price;
    this.packaging_price = result.packaging_price;
    this.postal_code = result.postal_code;
    this.country_code = result.country_code;
    this.cart_weight = result.cart_weight;
    this.total = result.total;
    this.is_gift = result.is_gift;
    this.recommendations = result.recommendations;
  }

  add_order_item(
    order_item: OrderItem,
    onCartChangesSaved: CartChangedHandler
  ): ?XMLHttpRequest {
    if (this.has_order_item(order_item)) {
      return null;
    }

    this.order_items.push(order_item);
    return request(
      `/api/v1/cart/${this.id}/${order_item.id}`,
      "PUT",
      (result: CartPayload) => {
        this.load_from_server(result);

        if (onCartChangesSaved) {
          onCartChangesSaved(result);
        }
      }
    );
  }

  get_url(): string {
    return "/cart/" + this.id;
  }

  has_order_item(order_item: OrderItem): boolean {
    return (
      !isNil(order_item.id) && some(this.order_items, { id: order_item.id })
    );
  }

  remove_order_item(
    order_item: OrderItem,
    callback: CartChangedHandler
  ): XMLHttpRequest {
    // $FlowIgnore: lodash has a prediction shorthand which is not defined in type lib.
    this.order_items = without<OrderItem>(this.order_items, {
      id: order_item.id
    });
    return request(
      `/api/v1/cart/${this.id}/${order_item.id}`,
      "DELETE",
      (result: CartPayload) => {
        this.load_from_server(result);
        callback(result);
      }
    );
  }

  populate_with_server_data(onSuccess: () => void): XMLHttpRequest {
    const callback = (result: CartPayload) => {
      this.load_from_server(result);
      onSuccess();
    };

    return isUndefined(this.id)
      ? post(`/api/v1/cart/do_create_guest_cart`, {}, callback)
      : get(`/api/v1/cart/${this.id}`, {}, callback);
  }

  tojson(): CartPayload {
    return pick(this, ["id", "order_items", "subtotal", "shipping", "total"]);
  }

  update_postal_details(
    postal_payload: postal_payload,
    callback: CartChangedHandler
  ): XMLHttpRequest {
    let url: string = `/api/v1/cart/${this.id}/do_update_postal_details`;
    return post(url, postal_payload, (result: CartPayload) => {
      this.load_from_server(result);
      callback(result);
    });
  }
}
